// Italian (tu) copy for /web-uploader, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "L'uploader web" for the web uploader; "anteprime" and "originali".
// The uploader's own labels are in English, so they're quoted as it shows them («Previews», «Originals»).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const it: WebUploaderCopy = {
  seo: {
    title: "L'uploader web di Photohawk | Più veloce dell'FTP",
    description:
      "Abbiamo ricostruito l'uploader web di Photohawk. Invia molte foto alla volta, gestisce caricamenti di oltre 100.000 foto e riprende da solo se la connessione cade, con tutti i piani. Con tutti i piani le anteprime leggere vengono caricate per prime, così la tua galleria è online in pochi minuti, non in ore.",
  },
  hero: {
    badge: "Novità",
    eyebrow: "Con tutti i piani",
    title: "Il nuovo uploader web di Photohawk",
    subtitle:
      "Più veloce dell'FTP, pensato per caricamenti di oltre 100.000 foto, e riprende da dove si era fermato.",
    plans: "Con tutti i piani, grazie all'anteprima prima un grande evento è online in pochi minuti, non in ore.",
    ctaPrimary: "Apri l'uploader web",
    ctaSecondary: "Scopri le novità",
    screenshotAlt:
      "L'uploader web durante un caricamento. Con l'anteprima prima (tutti i piani), la barra di stato indica che la galleria è online con le anteprime e mostra quante persone la stanno visitando, e la coda a destra mostra le anteprime completate e gli originali in arrivo, con due foto acquistate che passano per prime.",
    phoneAlt: "L'uploader web su uno smartphone, aperto in una cartella della galleria, con lo stato del caricamento in basso.",
  },
  previews: {
    heading: "Prima le anteprime",
    badge: "Tutti i piani",
    subtitle:
      "Con tutti i piani: online in pochi minuti, non in ore. Una galleria di 10.000 foto può essere online e vendere in circa 8 minuti con una velocità di upload di 20 Mbps.",
    body: "Le anteprime leggere vengono caricate per prime, così la tua galleria può andare online e iniziare a vendere, o a offrire download gratuiti, mentre arrivano gli originali. Se qualcuno acquista o scarica una foto prima che arrivi il suo originale, quell'originale passa in testa alla coda.",
    steps: [
      { id: "previews", text: "Le anteprime leggere vanno su per prime" },
      { id: "live", text: "La tua galleria va online" },
      { id: "buy", text: "Qualcuno acquista una foto" },
      { id: "jump", text: "Il suo originale passa in testa alla coda" },
      { id: "originals", text: "Seguono gli originali" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Illustrazione animata: prima le anteprime",
      description:
        "Le anteprime leggere di sei foto volano dal tuo computer alla tua galleria, e la galleria passa a «Live». Qualcuno tocca «Buy» su una foto: il suo originale passa in testa alla coda degli originali e viene caricato per primo. Gli altri originali seguono.",
    },
    link: "Come funzionano i caricamenti con anteprima prima",
  },
  faster: {
    heading: "Perché è più veloce dell'FTP",
    subtitle: "Le stesse foto, inviate in due modi.",
    anim: {
      ...en.faster.anim,
      label: "Confronto animato tra un programma FTP e l'uploader web",
      description:
        "Due righe. In alto, un programma FTP invia le foto 2 alla volta di default, e non più di 10. In basso, l'uploader web invia molte foto alla volta su corsie che si allargano e si restringono in base alla connessione. Con tutti i piani le anteprime leggere partono per prime, quindi la sua galleria è online prima. La connessione cade per un momento e il caricamento riprende da solo.",
      legendPreviews: "Anteprime",
      legendOriginals: "Originali",
    },
    points: [
      {
        id: "parallel",
        title: "Molte foto alla volta",
        body: "Invia molte foto alla volta, molte più di un programma FTP come FileZilla (2 alla volta di default, e non più di 10).",
      },
      {
        id: "adaptive",
        title: "Sfrutta al massimo la tua connessione",
        body: "Si regola di continuo mentre carica, per sfruttare al massimo la tua connessione.",
      },
      {
        id: "scale",
        title: "Pensato per i grandi eventi",
        body: "L'abbiamo testato su larga scala e gestisce senza fatica caricamenti di oltre 100.000 foto.",
      },
      {
        id: "live",
        title: "Online in pochi minuti, non in ore (tutti i piani)",
        body: "Con tutti i piani le anteprime leggere vengono caricate per prime, così una galleria di 10.000 foto può essere online e vendere in circa 8 minuti con una velocità di upload di 20 Mbps, mentre arrivano gli originali.",
      },
      {
        id: "resume",
        title: "Riprende da dove si era fermato",
        body: "Se la connessione cade, riprende da solo. Aggiungi di nuovo la cartella e viene inviato solo quello che manca.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Quanto durerà il tuo caricamento?",
    subtitle: "Una stima per la tua galleria, in base alla tua velocità di upload.",
    heroLink: "Stima il tempo di caricamento",
    photos: "Numero di foto",
    custom: "Altro",
    customLabel: "Numero di foto (altro)",
    size: "Dimensione media di un originale",
    sizeUnit: "MB",
    sizeHint: "I JPEG degli eventi sono spesso di circa {mb} MB.",
    speed: "La tua velocità di upload",
    speedUnit: "Mbps",
    test: "Testa la mia velocità di upload",
    testing: "Test in corso… {mbps} Mbps",
    testStarting: "Test in corso…",
    testDone: "Misurata: {mbps} Mbps in upload",
    testFailed: "Non siamo riusciti a misurare la tua velocità in questo momento. Inseriscila tu, oppure prova un test di velocità generico.",
    dataNote: "Usa fino a circa {mb} MB di dati, e solo quando tocchi il pulsante.",
    otherTest: "Oppure usa un test di velocità generico",
    resultsHeading: "La tua stima",
    previewsLabel: "Prima le anteprime",
    previewsPlans: "Tutti i piani",
    previewsLive: "Galleria online in {time}",
    previewsAll: "Tutti gli originali caricati in {time}",
    originalsLabel: "Solo originali",
    originalsLive: "Galleria online in {time}",
    originalsWhen: "quando tutti i {gb} GB di originali sono caricati",
    computerNote: "A questa velocità, il ritmo lo detta il tuo computer: quanto velocemente crea le anteprime.",
    disclaimer: "Sono solo stime. I tempi reali dipendono dalla tua connessione, dal tuo computer e dalle tue foto.",
    time: {
      under: "meno di un minuto",
      min: "circa {n} min",
      h: "circa {n} h",
      d: "circa {n} giorni",
    },
    table: {
      caption: "Esempio: 10.000 foto",
      speed: "Velocità di upload",
      live: "Prima le anteprime: galleria online (tutti i piani)",
      all: "Tutti gli originali caricati",
      note: "Il dato di circa 8 minuti è stato misurato con una velocità di upload di 20 Mbps (prima le anteprime). Le altre righe sono stime dello stesso modello, con originali di circa {mb} MB.",
    },
  },
  features: {
    heading: "Altre novità",
    subtitle: "Schermate reali del nuovo uploader web.",
    items: [
      {
        id: "status",
        title: "Lo stato, in parole semplici",
        body: "Vedi a colpo d'occhio a che punto sei. Con l'anteprima prima (tutti i piani): «Previews» (anteprime), poi «Originals» (originali), ognuno con il suo avanzamento.",
        alt: "Lo stato del caricamento con l'anteprima prima (tutti i piani): «Live — saving originals…», una riga di avanzamento per «Previews» e una per «Originals», e «2 photos people bought go first».",
      },
      {
        id: "queue",
        title: "Una coda che va dove vuoi tu",
        body: "Aggancia la coda di caricamento a destra, a sinistra o in basso, e trascinala alla dimensione che preferisci. Il browser ricorda la tua scelta.",
        alt: "La coda di caricamento agganciata in basso nell'uploader, con il menu aperto per spostarla a destra, in basso o a sinistra.",
      },
      {
        id: "visitors",
        title: "Guarda chi sta guardando",
        body: "La barra di stato mostra quante persone stanno guardando la tua galleria in questo momento, con tutti i piani.",
        alt: "La barra di stato in alto nell'uploader: «Live on previews» (con l'anteprima prima) e il numero di visitatori.",
      },
      {
        id: "resume",
        title: "Riprende da dove si era fermato",
        body: "Se la connessione cade, riprende da solo. Aggiungi di nuovo la cartella e viene inviato solo quello che manca.",
        alt: "Un avviso nell'uploader che indica che il caricamento di una cartella non è terminato, con un pulsante «Resume».",
      },
      {
        id: "phone",
        title: "Chiaro anche sullo smartphone",
        body: "Su uno schermo piccolo il percorso delle cartelle si compatta per starci, e toccando «…» vedi le cartelle intermedie. Così sai sempre dove stanno andando le tue foto.",
        alt: "L'uploader su uno smartphone: il percorso mostra la cartella corrente e «…» apre l'elenco delle cartelle superiori.",
      },
    ],
  },
  ftp: {
    date: "1° novembre 2026",
    heading: "Cosa cambia per l'FTP il 1° novembre",
    body: "I nostri server FTP condivisi sempre attivi chiudono il 1° novembre 2026. Usi l'FTP oggi? Ti chiediamo di cambiare prima di quella data.",
    points: [
      {
        id: "closes",
        title: "L'FTP condiviso chiude il 1° novembre 2026",
        body: "I server FTP condivisi sempre attivi si spengono il 1° novembre 2026.",
      },
      {
        id: "standard",
        title: "L'uploader web è il modo standard per caricare",
        body: "È incluso in tutti i piani e puoi usarlo già oggi.",
      },
      {
        id: "ondemand",
        title: "Se l'FTP è indispensabile",
        body: "Con tutti i piani, chiunque nella tua area di lavoro può avviare un proprio server FTP su richiesta dall'uploader web, con un indirizzo personale che non cambia mai. Si spegne dopo un'ora di inattività.",
      },
    ],
    helpLink: "Come funziona l'FTP su richiesta",
  },
  more: {
    previewFirstTitle: "Come funzionano i caricamenti con anteprima prima",
    previewFirstBody: "Con tutti i piani. Passo dopo passo: le anteprime, la messa online e cosa vedono gli acquirenti.",
  },
  cta: {
    heading: "Provalo al tuo prossimo caricamento",
    body: "Il nuovo uploader web è pronto con tutti i piani. Aggiungi una cartella e guarda la tua galleria andare online.",
    button: "Apri l'uploader web",
  },
  pause: "Metti in pausa l'animazione",
  play: "Riproduci l'animazione",
};

export default it;
